import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import { ArrowBack, Edit, Print } from '@mui/icons-material';
import { fetchCustomerRequestByProjectCode, fetchCustomerRequestDetails, fetchModelDetails } from '../../services/dataService';
import { useAuth } from '../../context/AuthContext';
import { checkEditPermissions } from '../../services/editPermissionService';
import usePageTitle from '../../utils/usePageTitle';

const CustomerRequestDetails = () => {
  const { id } = useParams();
  const projectCode = id;
  usePageTitle(`Customer Request - ${projectCode}`);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [editPermission, setEditPermission] = useState({ canEdit: false, message: null });



  const loadProjectData = useCallback(async () => {
    if (!projectCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Try the dedicated details endpoint first, fallback to the regular endpoint
      let data;
      try {
        data = await fetchCustomerRequestDetails(projectCode);
      } catch (detailsError) {
        console.log('Details endpoint failed, trying regular endpoint:', detailsError);
        data = await fetchCustomerRequestByProjectCode(projectCode);
      }
      
      // Always try to fetch models separately to ensure we get model data
      try {
        const models = await fetchModelDetails(projectCode);
        if (Array.isArray(models) && models.length > 0) {
          data.models = models;
        }
      } catch (modelError) {
        console.log('Model fetch failed:', modelError);
        // Ensure models array exists even if empty
        if (!data.models || !Array.isArray(data.models)) {
          data.models = [];
        }
      }
      
      console.log('Final project data with models:', data);
      setProjectData(data);
      
      // Check edit permissions
      const permission = checkEditPermissions(data, isAdmin);
      setEditPermission(permission);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load project details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [projectCode, isAdmin]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACT':
      case 'ACTIVE':
        return 'success';
      case 'TER':
      case 'TERMINATED':
        return 'error';
      case 'CLO':
      case 'CLOSED':
        return 'default';
      case 'INV':
      case 'INVOICED':
        return 'info';
      case 'APP':
      case 'APPROVED':
        return 'primary';
      case 'INS':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACT':
        return 'Active';
      case 'TER':
        return 'Terminated';
      case 'CLO':
        return 'Closed';
      case 'INV':
        return 'Invoiced';
      case 'APP':
        return 'Approved';
      case 'INS':
        return 'Ready for Invoice';
      default:
        return status || 'Unknown';
    }
  };

  const getWorkTypeName = (typeCode) => {
    if (!typeCode) return 'N/A';
    
    const workTypes = {
      'AUD': 'Audit',
      'CET': 'CET',
      'LAB': 'Labeling',
      'REW': 'Rework',
      'SCR': 'Screening',
      'SQV': 'SQV',
      'UPG': 'Upgrade',
      'CFG': 'Configuration',
      'RBX': 'Rebox',
      'C55': 'CD55',
      'BXR': 'Rebox',
      'PZG': 'Palletizing',
      'DFG': 'Deconfiguration',
      'STG': 'Sorting',
      'PCI': 'Power Cord Insert',
      'BDG': 'Bundling'
    };
    return workTypes[typeCode.toUpperCase()] || typeCode;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/customer-request')}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!projectData) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/customer-request')}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="warning">
          Project not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} className="print-content">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/customer-request')}
            sx={{ mr: 2 }}
            className="no-print"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Customer Request {projectCode}
          </Typography>
          {projectData.status && (
            <Chip
              label={getStatusLabel(projectData.status)}
              color={getStatusColor(projectData.status)}
              sx={{ ml: 2 }}
              className="no-print"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print
          </Button>
          {isAdmin && (
            <Tooltip title={editPermission.message || ''} arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  disabled={!editPermission.canEdit}
                  onClick={() => {
                    if (editPermission.canEdit) {
                      navigate(`/customer-request/${projectCode}/edit`);
                    }
                  }}
                >
                  Edit Project
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box className="print-header" sx={{ display: 'none', '@media print': { display: 'block !important' } }}>
        Customer Request Form - {projectCode}
      </Box>

      <Paper sx={{ p: 6, mb: 3, width: '100%', maxWidth: '100%' }} elevation={2} component="section" aria-labelledby="project-info-heading">
        <Typography variant="h5" component="h2" id="project-info-heading" sx={{ mb: 3, fontWeight: 600 }}>
          Project Information
        </Typography>
        <Grid container spacing={4} sx={{ width: '100%', maxWidth: '100%' }}>
          {/* Row 1: Location */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Location
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.location || projectData.project_location || projectData.loc || 
               (projectData.location_code === 'C' ? 'Carson' : 
                projectData.location_code === 'Q' ? 'QA' : 
                projectData.location_code === 'P' ? 'PSA' : 
                projectData.location_code || 'Carson')}
            </Typography>
          </Grid>

          {/* Row 2: Requestor Name & Business Group */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Requestor Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.requestorName || projectData.requestor_name || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Business Group
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.businessGroup || projectData.group_name || 'N/A'}
            </Typography>
          </Grid>

          {/* Row 3: Budget Code & Full Customer Name */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Budget Code
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.budgetCode || projectData.budget_code || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Full Customer Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.fullCustomerName || projectData.cust_name || 'N/A'}
            </Typography>
          </Grid>

          {/* Row 4: Department & Business Name */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Department
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.department || projectData.dept_name || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Business Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.businessName || projectData.address_1 || 'N/A'}
            </Typography>
          </Grid>

          {/* Row 5: Street Address & City State Zip Code */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Street Address
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.streetAddress || projectData.address_2 || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              City, State, Zip Code
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.cityStateZip || projectData.address_3 || 'N/A'}
            </Typography>
          </Grid>

          {/* Row 6: PIC & Email Address */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              PIC
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.picContact || projectData.contact || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Email Address
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {projectData.emailAddress || projectData.email_address || 'N/A'}
            </Typography>
          </Grid>

          {/* Row 7: Type of Work */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Type of Work
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '32px', fontSize: '1.1rem', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {getWorkTypeName(projectData.typeOfWork || projectData.work_type)}
            </Typography>
          </Grid>

          {/* Row 8: Work to be Performed */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Work to be Performed
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '48px', fontSize: '1.1rem', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, wordWrap: 'break-word' }}>
              {projectData.workToBePerformed || projectData.description || projectData.project_description || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Row 9: Model Detail Table */}
      {projectData.models && projectData.models.length > 0 ? (
        <Paper sx={{ p: 6, mb: 3, width: '100%' }} elevation={2} component="section" aria-labelledby="model-details-heading">
          <Typography variant="h5" component="h2" id="model-details-heading" sx={{ mb: 3, fontWeight: 600 }}>
            Model Details
          </Typography>
          
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 150 }}>Model</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 120 }}>To</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 100 }}>Quantity</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 140 }}>Date Required</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 140 }}>Date of Job</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 100 }}>Line</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', p: 2, minWidth: 150 }}>PIC</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectData.models.map((model, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                      height: 60
                    }}
                  >
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{model.model || model.from_model || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{model.to || model.to_model || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{model.quantity || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{formatDate(model.dateRequired || model.required_dt)}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{formatDate(model.dateOfJobLine || model.date_of_job_line)}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{model.lineNum || model.line_num || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '1rem', p: 2 }}>{model.personInCharge || model.pic_name || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper sx={{ p: 6, mb: 3, width: '100%' }} elevation={2} component="section" aria-labelledby="model-details-heading">
          <Typography variant="h5" component="h2" id="model-details-heading" sx={{ mb: 3, fontWeight: 600 }}>
            Model Details
          </Typography>
          <Alert severity="info" sx={{ fontSize: '1.1rem', p: 2 }}>
            No model details found for this project.
          </Alert>
        </Paper>
      )}

      {/* Row 10: Problem Description and Comments */}
      <Paper sx={{ p: 6, mb: 3, width: '100%', maxWidth: '100%' }} elevation={2} component="section" aria-labelledby="problem-description-heading">
        <Typography variant="h5" component="h2" id="problem-description-heading" sx={{ mb: 3, fontWeight: 600 }}>
          Problem Description
        </Typography>
        <Grid container spacing={4} sx={{ width: '100%', maxWidth: '100%' }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Problem Description and Comments
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '48px', fontSize: '1.1rem', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, wordWrap: 'break-word' }}>
              {projectData.problemDescription || projectData.comments || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Row 11: Carson Comments */}
      <Paper sx={{ p: 6, mb: 3, width: '100%', maxWidth: '100%' }} elevation={2} component="section" aria-labelledby="internal-comments-heading">
        <Typography variant="h5" component="h2" id="internal-comments-heading" sx={{ mb: 3, fontWeight: 600 }}>
          Internal Comments
        </Typography>
        <Grid container spacing={4} sx={{ width: '100%', maxWidth: '100%' }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Carson Comments
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, minHeight: '48px', fontSize: '1.1rem', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, wordWrap: 'break-word' }}>
              {projectData.carsonComments || projectData.internal_comments || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CustomerRequestDetails;